// Brand Ambassador bookings — the coordinator workspace (BA P2.4a,
// `services:booking:manage`, behind BaManageGate's rule via BaRoute).
// Bookings with their slots, quotes, invitations, funding evidence and the
// confirm panel; clients; offerings; cancellation / no-show terms.
//
// Coordinators act for clients in the pilot (ruling D): there is no buyer or
// client sign-in screen anywhere here. Commerce answers 404 while booking is off
// or the company isn't entitled — shown as a plain "not available" state.
import React, { useCallback, useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { Input } from '@/features/shared/components/ui/input';
import { DataState } from '@/features/shared/components/ui/DataState';
import { useRoleAccess } from '@/features/hooks/useRoleAccess';
import { getApiErrorMessage } from '@/lib/apiError';
import {
  type Booking, type BookingClient, type Offering, type Terms, createBooking, errorStatus, listBookings, listClients,
  listOfferings, listTerms,
} from '@/services/baBookingService';
import { BOOKING_DEFAULT_TZ, formatInZone, zonedLocalToIso } from './bookingFormat';
import { BookingDetail } from './BookingDetail';
import { ClientsPanel, OfferingsPanel, TermsPanel } from './CoordinatorPanels';
import { Field, type K, NativeSelect, StatusBadge, useBk, useRunAction } from './shared';

type Tab = 'bookings' | 'clients' | 'offerings' | 'terms';
type Data = { bookings: Booking[]; clients: BookingClient[]; offerings: Offering[]; terms: Terms[] };

export const BaBookingsPage: React.FC = () => {
  const { k } = useBk();
  const { isTenantOwner } = useRoleAccess();
  const [tab, setTab] = useState<Tab>('bookings');
  const [state, setState] = useState<{ s: 'loading' } | { s: 'ok'; d: Data } | { s: 'unavailable' } | { s: 'denied' } | { s: 'error'; msg: string }>({ s: 'loading' });
  const [selected, setSelected] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(() => {
    Promise.all([listBookings(), listClients(), listOfferings(), listTerms()])
      .then(([bookings, clients, offerings, terms]) => setState({ s: 'ok', d: { bookings, clients, offerings, terms } }))
      .catch((e) => {
        const st = errorStatus(e);
        setState(st === 404 ? { s: 'unavailable' } : st === 403 ? { s: 'denied' }
          : { s: 'error', msg: getApiErrorMessage(e, k('errors.load', 'Could not load bookings.')) });
      });
  }, [k]);
  useEffect(() => { setState({ s: 'loading' }); load(); }, [load]);

  const tabs: { key: Tab; label: string }[] = [
    { key: 'bookings', label: k('tabs.bookings', 'Bookings') },
    { key: 'clients', label: k('tabs.clients', 'Clients') },
    { key: 'offerings', label: k('tabs.offerings', 'Offerings') },
    { key: 'terms', label: k('tabs.terms', 'Terms') },
  ];

  return (
    <div className="space-y-4 p-4 md:p-6" data-testid="ba-bookings-page">
      <div>
        <h1 className="text-h2 text-foreground">{k('title', 'Brand Ambassador bookings')}</h1>
        <p className="text-sm text-muted-foreground">
          {k('subtitle', 'Book Brand Ambassadors for your clients: quote, record the client’s acceptance and funding evidence, invite ambassadors, then confirm. KaziPeople never moves the money.')}
        </p>
      </div>

      {state.s === 'loading' && <Card><CardContent><DataState variant="loading" /></CardContent></Card>}
      {state.s === 'unavailable' && <Card><CardContent><DataState variant="unavailable" message={k('unavailable', 'Brand Ambassador booking isn’t available for this company.')} /></CardContent></Card>}
      {state.s === 'denied' && <Card><CardContent><DataState variant="denied" /></CardContent></Card>}
      {state.s === 'error' && <Card><CardContent><DataState variant="error" message={state.msg} onRetry={load} /></CardContent></Card>}

      {state.s === 'ok' && (
        <>
          <div className="flex flex-wrap gap-2" role="tablist">
            {tabs.map((t) => (
              <Button key={t.key} size="sm" role="tab" aria-selected={tab === t.key} variant={tab === t.key ? 'default' : 'outline'}
                onClick={() => setTab(t.key)}>{t.label}</Button>
            ))}
          </div>

          {tab === 'bookings' && (
            <div className="grid gap-4 lg:grid-cols-[22rem_1fr]">
              <Card><CardContent className="p-0">
                <div className="p-3 border-b border-border">
                  <Button size="sm" variant="outline" onClick={() => { setCreating(true); setSelected(null); }}
                    disabled={state.d.clients.length === 0 || state.d.offerings.length === 0}>
                    <Plus className="w-4 h-4 mr-1" />{k('booking.new', 'New booking')}
                  </Button>
                  {(state.d.clients.length === 0 || state.d.offerings.length === 0) && (
                    <p className="text-xs text-muted-foreground mt-1">{k('booking.need_setup', 'Add a client and an offering first.')}</p>
                  )}
                </div>
                {state.d.bookings.length === 0
                  ? <DataState variant="empty" title={k('booking.empty', 'No bookings yet.')} />
                  : (
                    <ul>{state.d.bookings.map((b) => (
                      <li key={b.id}>
                        <button type="button" onClick={() => { setSelected(b.id); setCreating(false); }}
                          className={`w-full text-left px-4 py-3 border-b border-border hover:bg-muted ${selected === b.id ? 'bg-muted' : ''}`}>
                          <p className="text-sm font-medium text-foreground">{b.eventTitle}</p>
                          <div className="flex flex-wrap items-center gap-2 mt-1">
                            <StatusBadge status={b.status} k={k} />
                            <span className="text-xs text-muted-foreground">{formatInZone(b.boardingAt, b.timezone)}</span>
                          </div>
                        </button>
                      </li>
                    ))}</ul>
                  )}
              </CardContent></Card>
              <div>
                {creating && (
                  <NewBookingForm k={k} clients={state.d.clients} offerings={state.d.offerings}
                    onDone={(b) => { setCreating(false); setSelected(b.id); load(); }} onCancel={() => setCreating(false)} />
                )}
                {!creating && selected && (
                  <BookingDetail key={selected} bookingId={selected} k={k} clients={state.d.clients} terms={state.d.terms}
                    onChanged={load} />
                )}
                {!creating && !selected && <Card><CardContent><DataState variant="empty" title={k('booking.pick', 'Pick a booking.')} /></CardContent></Card>}
              </div>
            </div>
          )}
          {tab === 'clients' && <ClientsPanel k={k} clients={state.d.clients} onChange={load} />}
          {tab === 'offerings' && <OfferingsPanel k={k} offerings={state.d.offerings} onChange={load} />}
          {tab === 'terms' && <TermsPanel k={k} terms={state.d.terms} offerings={state.d.offerings} isOwner={!!isTenantOwner} onChange={load} />}
        </>
      )}
    </div>
  );
};

const NewBookingForm: React.FC<{
  k: K; clients: BookingClient[]; offerings: Offering[]; onDone: (b: Booking) => void; onCancel: () => void;
}> = ({ k, clients, offerings, onDone, onCancel }) => {
  const { busy, run } = useRunAction(k);
  const [f, setF] = useState({
    clientId: clients.length === 1 ? clients[0].id : '', offeringId: offerings.length === 1 ? offerings[0].id : '',
    eventTitle: '', venue: '', timezone: BOOKING_DEFAULT_TZ, boarding: '', departure: '', ret: '',
  });
  const set = (n: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setF((s) => ({ ...s, [n]: e.target.value }));
  const ready = f.clientId && f.offeringId && f.eventTitle.trim() && f.venue.trim() && f.timezone.trim() && f.boarding && f.departure && f.ret;
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ready) return;
    const tz = f.timezone.trim();
    const out = await run(() => createBooking({
      clientId: f.clientId, offeringId: f.offeringId, eventTitle: f.eventTitle.trim(), venue: f.venue.trim(), timezone: tz,
      boardingAt: zonedLocalToIso(f.boarding, tz), departureAt: zonedLocalToIso(f.departure, tz), returnAt: zonedLocalToIso(f.ret, tz),
    }), k('booking.created', 'Booking draft created.'));
    if (out) onDone(out);
  };
  return (
    <Card><CardContent className="pt-6">
      <form onSubmit={submit} className="space-y-3" data-testid="booking-form">
        <div className="grid gap-3 md:grid-cols-2">
          <Field label={k('booking.client', 'Client')} htmlFor="bk-client">
            <NativeSelect id="bk-client" value={f.clientId} onChange={set('clientId')} required>
              <option value="">{k('common.choose', 'Choose…')}</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.legalName}</option>)}
            </NativeSelect>
          </Field>
          <Field label={k('booking.offering', 'Offering')} htmlFor="bk-off">
            <NativeSelect id="bk-off" value={f.offeringId} onChange={set('offeringId')} required>
              <option value="">{k('common.choose', 'Choose…')}</option>
              {offerings.map((o) => <option key={o.id} value={o.id}>{o.title}</option>)}
            </NativeSelect>
          </Field>
          <Field label={k('booking.event_title', 'Event')} htmlFor="bk-title"><Input id="bk-title" value={f.eventTitle} onChange={set('eventTitle')} required /></Field>
          <Field label={k('booking.venue', 'Venue')} htmlFor="bk-venue"><Input id="bk-venue" value={f.venue} onChange={set('venue')} required /></Field>
          <Field label={k('booking.timezone', 'Time zone')} htmlFor="bk-tz" hint={k('booking.timezone_hint', 'The times below are in this zone, e.g. Africa/Dar_es_Salaam.')}>
            <Input id="bk-tz" value={f.timezone} onChange={set('timezone')} required />
          </Field>
          <Field label={k('booking.boarding', 'Boarding')} htmlFor="bk-board"><Input id="bk-board" type="datetime-local" value={f.boarding} onChange={set('boarding')} required /></Field>
          <Field label={k('booking.departure', 'Departure')} htmlFor="bk-dep"><Input id="bk-dep" type="datetime-local" value={f.departure} onChange={set('departure')} required /></Field>
          <Field label={k('booking.return', 'Return')} htmlFor="bk-ret"><Input id="bk-ret" type="datetime-local" value={f.ret} onChange={set('ret')} required /></Field>
        </div>
        <div className="flex gap-2">
          <Button type="submit" disabled={busy || !ready}>{k('booking.create', 'Create booking draft')}</Button>
          <Button type="button" variant="outline" onClick={onCancel}>{k('common.cancel', 'Cancel')}</Button>
        </div>
      </form>
    </CardContent></Card>
  );
};

export default BaBookingsPage;
